// Touchpoint detail panel — Phase-1 chunk.
//
// Surfaced under ``/dashboard/accounts/:id?tab=touchpoints&touchpoint=:id``
// via ``AccountTouchpointsTab``. Mirrors the v1 BeneCRM detail layout:
// header (account, kind, date, Edit), Details box, Action Items box,
// and a sidebar with Attendees, Recordings (Phase-1 stub), and
// Metadata. The Recordings + transcription pipeline lands in a
// follow-up chunk; this panel reserves the slot so deep-links don't
// flap when that wires up.

import { useMemo, useState } from "react";

import { Button, Modal } from "@/design-system";
import { useCommitmentListQuery } from "@/features/commitments/hooks";
import { useCommitmentModal } from "@/features/commitments/CommitmentModalProvider";
import type {
  CommitmentListRow,
} from "@/features/commitments/types";

import { fetchRecordingPlaybackUrl } from "./api";
import { TouchpointFormModal } from "./TouchpointFormModal";
import {
  useDeleteTouchpointRecordingMutation,
  useTouchpointDetailQuery,
  useTouchpointRecordingsQuery,
  useUpdateTouchpointMutation,
  useUploadTouchpointRecordingMutation,
} from "./hooks";
import type {
  Touchpoint,
  TouchpointCreateInput,
  TouchpointRecording,
  TouchpointStatus,
} from "./types";
import { TOUCHPOINT_KIND_LABEL, TOUCHPOINT_STATUS_LABEL } from "./types";


export interface TouchpointDetailPanelProps {
  accountId: string;
  accountName: string;
  touchpointId: string;
  onBack: () => void;
}


export function TouchpointDetailPanel({
  accountId, accountName, touchpointId, onBack,
}: TouchpointDetailPanelProps) {
  const detailQuery = useTouchpointDetailQuery(touchpointId);
  const updateMutation = useUpdateTouchpointMutation();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  if (detailQuery.isPending) {
    return (
      <p
        data-testid="touchpoint-detail-loading"
        className="px-3 py-6 text-xs text-text-tertiary"
      >
        Loading…
      </p>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div
        data-testid="touchpoint-detail-error"
        className="flex flex-col gap-2 rounded-md border border-border-subtle bg-canvas/40 p-4 text-xs text-ember-ink"
      >
        <p>Couldn&rsquo;t load this Touchpoint.</p>
        <button
          type="button"
          onClick={onBack}
          className="self-start font-medium text-teal hover:underline"
        >
          Back to list
        </button>
      </div>
    );
  }

  const tp = detailQuery.data;
  const onStatusChange = (next: TouchpointStatus) => {
    updateMutation.mutate({
      touchpoint_id: tp.touchpoint_id,
      account_id: accountId,
      values: { status: next },
    });
  };
  const onSubmitEdit = (values: TouchpointCreateInput) => {
    setEditError(null);
    updateMutation.mutate(
      {
        touchpoint_id: tp.touchpoint_id,
        account_id: accountId,
        values,
      },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setEditError(describeError(err)),
      },
    );
  };

  return (
    <section
      data-testid="touchpoint-detail-panel"
      className="flex flex-col gap-4"
    >
      <DetailHeader
        accountName={accountName}
        tp={tp}
        onBack={onBack}
        onEdit={() => { setEditError(null); setEditing(true); }}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="flex flex-col gap-4">
          <DetailsCard
            tp={tp}
            accountName={accountName}
            onStatusChange={onStatusChange}
            statusSaving={updateMutation.isPending}
          />
          <ActionItemsCard
            tp={tp}
            accountId={accountId}
            accountName={accountName}
          />
        </div>

        <aside className="flex flex-col gap-4">
          <AttendeesCard participants={tp.participants ?? []} />
          <RecordingsCard
            touchpointId={tp.touchpoint_id}
            accountId={accountId}
            initialRecordings={tp.recordings ?? []}
          />
          <MetadataCard tp={tp} />
        </aside>
      </div>

      <TouchpointFormModal
        open={editing}
        mode="edit"
        touchpoint={tp}
        saving={updateMutation.isPending}
        error={editError}
        onClose={() => setEditing(false)}
        onSubmit={onSubmitEdit}
      />
    </section>
  );
}


function DetailHeader({
  accountName, tp, onBack, onEdit,
}: {
  accountName: string;
  tp: Touchpoint;
  onBack: () => void;
  onEdit: () => void;
}) {
  const when = formatDateTime(tp.scheduled_at ?? tp.occurred_at);
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0">
        <button
          type="button"
          data-testid="touchpoint-detail-back"
          onClick={onBack}
          aria-label="Back to Touchpoints list"
          className="mt-1 rounded-md p-1 text-text-tertiary hover:bg-recessed hover:text-text-primary"
        >
          ←
        </button>
        <div className="flex flex-col gap-0.5 min-w-0">
          <h2
            data-testid="touchpoint-detail-title"
            className="text-lg font-semibold tracking-tight text-text-primary truncate"
          >
            {accountName}
          </h2>
          <p className="text-xs text-text-secondary">
            <span className="font-medium">{TOUCHPOINT_KIND_LABEL[tp.kind]}</span>
            {when && <> &middot; {when}</>}
          </p>
        </div>
      </div>
      <Button
        variant="primary"
        data-testid="touchpoint-detail-edit"
        onClick={onEdit}
      >
        Edit
      </Button>
    </header>
  );
}


function DetailsCard({
  tp, accountName, onStatusChange, statusSaving,
}: {
  tp: Touchpoint;
  accountName: string;
  onStatusChange: (next: TouchpointStatus) => void;
  statusSaving: boolean;
}) {
  return (
    <article
      data-testid="touchpoint-detail-details"
      className="flex flex-col gap-4 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <h3 className="text-sm font-semibold text-text-primary">Details</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Client" value={accountName} />
        <Field label="Type" value={TOUCHPOINT_KIND_LABEL[tp.kind]} />
        <Field label="Date &amp; Time" value={formatDateTime(tp.scheduled_at)} />
        <Field
          label="Duration"
          value={
            tp.duration_minutes != null
              ? `${tp.duration_minutes} minutes`
              : "—"
          }
        />
        <Field label="Timezone" value={tp.time_zone || "—"} />
        <div className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-text-tertiary">
            Status
          </span>
          <select
            data-testid="touchpoint-detail-status"
            value={tp.status}
            disabled={statusSaving}
            onChange={(e) => onStatusChange(e.target.value as TouchpointStatus)}
            className="rounded-md border border-border-subtle bg-canvas/40 px-2 py-1.5 text-xs text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30 disabled:opacity-50"
          >
            {(["scheduled", "completed", "cancelled", "no_show"] as const).map(
              (s) => (
                <option key={s} value={s}>
                  {TOUCHPOINT_STATUS_LABEL[s]}
                </option>
              ),
            )}
          </select>
        </div>
      </div>
      <div className="flex flex-col gap-3 border-t border-border-subtle pt-3">
        <Field label="Agenda" value={tp.subject || "—"} multiline />
        <Field label="Meeting Notes" value={tp.body || "—"} multiline />
        {tp.outcome_summary && (
          <Field label="Outcome" value={tp.outcome_summary} multiline />
        )}
      </div>
    </article>
  );
}


function ActionItemsCard({
  tp, accountId, accountName,
}: {
  tp: Touchpoint;
  accountId: string;
  accountName: string;
}) {
  const modal = useCommitmentModal();
  const listQuery = useCommitmentListQuery({
    filters: { source_touchpoint_id: tp.touchpoint_id },
    sortBy: "due_date_time",
    sortDir: "asc",
    limit: 100,
    offset: 0,
  });
  const rows: CommitmentListRow[] = useMemo(
    () => listQuery.data?.rows ?? [],
    [listQuery.data],
  );

  const onAdd = () => {
    modal.openCreateCommitmentModal({
      defaults: {
        account_id: accountId,
        source_touchpoint_id: tp.touchpoint_id,
      },
      returnContext: {
        fromUrl:
          `/dashboard/accounts/${encodeURIComponent(accountId)}` +
          `?tab=touchpoints&touchpoint=${encodeURIComponent(tp.touchpoint_id)}`,
        fromLabel: `${accountName} — Touchpoint`,
      },
    });
  };

  return (
    <article
      data-testid="touchpoint-detail-action-items"
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <header className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-text-primary">
          Action Items ({rows.length})
        </h3>
        <Button
          variant="primary"
          data-testid="touchpoint-detail-add-task"
          onClick={onAdd}
        >
          + Add Task
        </Button>
      </header>
      {listQuery.isPending ? (
        <p className="text-xs text-text-tertiary">Loading…</p>
      ) : rows.length === 0 ? (
        <p
          data-testid="touchpoint-detail-action-items-empty"
          className="rounded-md border border-dashed border-border-subtle px-3 py-6 text-center text-xs text-text-tertiary"
        >
          No action items yet.
        </p>
      ) : (
        <ul role="list" className="flex flex-col gap-2">
          {rows.map((row) => (
            <ActionItemRow key={row.commitment_id} row={row} modal={modal} />
          ))}
        </ul>
      )}
    </article>
  );
}


function ActionItemRow({
  row, modal,
}: {
  row: CommitmentListRow;
  modal: ReturnType<typeof useCommitmentModal>;
}) {
  const due = row.due_date_time
    ? formatDateTime(row.due_date_time)
    : "No due date";
  return (
    <li
      data-testid={`touchpoint-detail-action-item-${row.commitment_id}`}
      className="flex items-start justify-between gap-3 rounded-md border border-border-subtle bg-raised px-3 py-2"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <button
          type="button"
          onClick={() => modal.openCommitmentModal(row.commitment_id)}
          className="text-left text-sm font-medium text-text-primary hover:text-teal hover:underline"
        >
          {row.title}
        </button>
        <span className="text-[11px] text-text-tertiary">
          {due}
          {row.status ? <> &middot; {row.status.replaceAll("_", " ")}</> : null}
        </span>
      </div>
    </li>
  );
}


function AttendeesCard({ participants }: { participants: string[] }) {
  return (
    <article
      data-testid="touchpoint-detail-attendees"
      className="flex flex-col gap-2 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <h3 className="text-sm font-semibold text-text-primary">
        Attendees ({participants.length})
      </h3>
      {participants.length === 0 ? (
        <p className="text-xs text-text-tertiary">No attendees</p>
      ) : (
        <ul role="list" className="flex flex-col gap-1">
          {participants.map((p) => (
            <li key={p} className="text-xs text-text-secondary">
              {p}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}


// MIME types Whisper accepts. The picker filter uses these for the
// browse dialog; the BE re-validates so a drag-and-drop bypassing
// the filter still gets rejected at upload time.
const ACCEPTED_RECORDING_TYPES =
  "audio/*,video/*,.mp3,.wav,.m4a,.mp4,.webm,.ogg";

const MAX_RECORDING_BYTES = 200 * 1024 * 1024;


function RecordingsCard({
  touchpointId, accountId, initialRecordings,
}: {
  touchpointId: string;
  accountId: string;
  initialRecordings: TouchpointRecording[];
}) {
  // Detail response embeds the initial list, but the dedicated query
  // gives us a clean cache key for the upload/delete mutations to
  // invalidate without flipping the larger touchpoint detail.
  const listQuery = useTouchpointRecordingsQuery(touchpointId);
  const uploadMutation = useUploadTouchpointRecordingMutation();
  const deleteMutation = useDeleteTouchpointRecordingMutation();
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const rows: TouchpointRecording[] =
    listQuery.data?.rows ?? initialRecordings;

  const handleFile = (file: File | null | undefined) => {
    setUploadError(null);
    if (!file) return;
    if (file.size > MAX_RECORDING_BYTES) {
      setUploadError(
        `File is too large (max ${Math.round(
          MAX_RECORDING_BYTES / 1024 / 1024,
        )} MB).`,
      );
      return;
    }
    uploadMutation.mutate(
      { touchpoint_id: touchpointId, account_id: accountId, file },
      {
        onError: (err) =>
          setUploadError(
            err instanceof Error ? err.message : "Upload failed.",
          ),
      },
    );
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0] ?? null;
    handleFile(f);
  };

  const onDelete = (rec: TouchpointRecording) => {
    if (!window.confirm("Delete this recording? This can't be undone.")) {
      return;
    }
    deleteMutation.mutate({
      recording_id: rec.recording_id,
      touchpoint_id: touchpointId,
      account_id: accountId,
    });
  };

  return (
    <article
      data-testid="touchpoint-detail-recordings"
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-canvas/40 p-4"
    >
      <h3 className="text-sm font-semibold text-text-primary">
        Recordings ({rows.length})
      </h3>

      <Dropzone
        active={dragActive}
        uploading={uploadMutation.isPending}
        onDragOver={(e) => {
          e.preventDefault(); setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onPick={(file) => handleFile(file)}
      />
      {uploadError && (
        <p
          role="alert"
          data-testid="touchpoint-recording-upload-error"
          className="text-xs font-medium text-ember-ink"
        >
          {uploadError}
        </p>
      )}

      {rows.length === 0 ? (
        <p
          data-testid="touchpoint-detail-recordings-empty"
          className="text-xs text-text-tertiary"
        >
          No recordings uploaded yet.
        </p>
      ) : (
        <ul role="list" className="flex flex-col gap-2">
          {rows.map((rec) => (
            <RecordingRow
              key={rec.recording_id}
              rec={rec}
              onDelete={() => onDelete(rec)}
              deleting={deleteMutation.isPending}
            />
          ))}
        </ul>
      )}
    </article>
  );
}


function Dropzone({
  active, uploading, onDragOver, onDragLeave, onDrop, onPick,
}: {
  active: boolean;
  uploading: boolean;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onPick: (file: File | null) => void;
}) {
  return (
    <div
      data-testid="touchpoint-recording-dropzone"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={[
        "flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-3 py-6 text-center text-xs",
        active
          ? "border-teal bg-teal-tint/20 text-text-primary"
          : "border-border-subtle text-text-tertiary",
      ].join(" ")}
    >
      {uploading ? (
        <p>Uploading…</p>
      ) : (
        <>
          <p>Drag and drop a recording here, or</p>
          <label className="cursor-pointer font-medium text-teal hover:underline">
            browse
            <input
              type="file"
              accept={ACCEPTED_RECORDING_TYPES}
              data-testid="touchpoint-recording-input"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                onPick(file);
                e.target.value = ""; // allow re-picking the same file
              }}
              className="hidden"
            />
          </label>
          <p className="text-text-tertiary">Supports MP3, WAV, MP4, WebM</p>
        </>
      )}
    </div>
  );
}


function RecordingRow({
  rec, onDelete, deleting,
}: {
  rec: TouchpointRecording;
  onDelete: () => void;
  deleting: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const onPlay = async () => {
    if (playbackUrl) {
      setPlaying(true);
      return;
    }
    setPlaybackError(null);
    try {
      const { url } = await fetchRecordingPlaybackUrl(rec.recording_id);
      setPlaybackUrl(url);
      setPlaying(true);
    } catch (err) {
      setPlaybackError(
        err instanceof Error ? err.message : "Couldn't load recording.",
      );
    }
  };

  const hasTranscript = Boolean(
    rec.transcript_text && rec.transcription_status === "succeeded",
  );

  return (
    <li
      data-testid={`touchpoint-recording-row-${rec.recording_id}`}
      className="flex flex-col gap-1 rounded-md border border-border-subtle bg-raised px-3 py-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-xs font-medium text-text-primary">
            {rec.original_filename}
          </span>
          <span className="text-[11px] text-text-tertiary">
            {formatBytes(rec.size_bytes)}
            {rec.duration_seconds != null
              ? ` · ${formatDuration(rec.duration_seconds)}`
              : ""}
            {" · "}
            <TranscriptionBadge status={rec.transcription_status} />
          </span>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2 text-[11px]">
          {hasTranscript && (
            <button
              type="button"
              data-testid={`touchpoint-recording-transcript-${rec.recording_id}`}
              onClick={() => setTranscriptOpen(true)}
              className="font-medium text-teal hover:underline"
            >
              Transcript
            </button>
          )}
          {!playing && (
            <button
              type="button"
              data-testid={`touchpoint-recording-play-${rec.recording_id}`}
              onClick={onPlay}
              className="font-medium text-teal hover:underline"
            >
              Play
            </button>
          )}
          <button
            type="button"
            data-testid={`touchpoint-recording-delete-${rec.recording_id}`}
            onClick={onDelete}
            disabled={deleting}
            className="font-medium text-ember-ink hover:underline disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
      {playing && playbackUrl && (
        <audio
          data-testid={`touchpoint-recording-audio-${rec.recording_id}`}
          src={playbackUrl}
          controls
          autoPlay
          className="w-full"
        />
      )}
      {playbackError && (
        <p
          role="alert"
          data-testid={`touchpoint-recording-play-error-${rec.recording_id}`}
          className="text-[11px] text-ember-ink"
        >
          {playbackError}
        </p>
      )}
      {rec.transcript_error && rec.transcription_status === "failed" && (
        <p className="text-[11px] text-ember-ink">
          Transcription failed. Reference{" "}
          <code className="font-mono">{rec.transcript_error}</code> —
          contact support if it persists.
        </p>
      )}
      {hasTranscript && (
        <TranscriptModal
          rec={rec}
          open={transcriptOpen}
          onClose={() => setTranscriptOpen(false)}
        />
      )}
    </li>
  );
}


function TranscriptModal({
  rec, open, onClose,
}: {
  rec: TouchpointRecording;
  open: boolean;
  onClose: () => void;
}) {
  const segments = rec.transcript_segments ?? [];
  const hasSegments = segments.length > 0;
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Transcript"
      footer={
        <Button
          variant="tertiary"
          onClick={onClose}
          data-testid={`touchpoint-recording-transcript-close-${rec.recording_id}`}
        >
          Close
        </Button>
      }
    >
      <div
        data-testid={`touchpoint-recording-transcript-modal-${rec.recording_id}`}
        className="flex flex-col gap-3"
      >
        <header className="flex flex-col gap-0.5 border-b border-border-subtle pb-2">
          <span className="text-sm font-medium text-text-primary">
            {rec.original_filename}
          </span>
          <span className="text-[11px] text-text-tertiary">
            {formatBytes(rec.size_bytes)}
            {rec.duration_seconds != null
              ? ` · ${formatDuration(rec.duration_seconds)}`
              : ""}
            {rec.transcribed_at
              ? ` · transcribed ${formatTranscribedDate(rec.transcribed_at)}`
              : ""}
          </span>
        </header>
        {hasSegments ? (
          <ol
            data-testid={`touchpoint-recording-transcript-segments-${rec.recording_id}`}
            className="max-h-[60vh] overflow-y-auto rounded-md bg-canvas/40 p-3 text-sm leading-relaxed text-text-primary"
          >
            {segments.map((seg, i) => (
              <li
                key={i}
                className="grid grid-cols-[64px_1fr] gap-3 py-1 first:pt-0 last:pb-0"
              >
                <span className="select-none font-mono text-[11px] text-text-tertiary">
                  {formatTimestamp(seg.start)}
                </span>
                <span className="whitespace-pre-wrap">{seg.text}</span>
              </li>
            ))}
          </ol>
        ) : (
          // Legacy rows transcribed before segments were captured fall
          // back to the joined plain text.
          <div
            data-testid={`touchpoint-recording-transcript-text-${rec.recording_id}`}
            className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap rounded-md bg-canvas/40 p-3 text-sm leading-relaxed text-text-primary"
          >
            {rec.transcript_text}
          </div>
        )}
      </div>
    </Modal>
  );
}


function formatTimestamp(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}


function formatTranscribedDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}


function TranscriptionBadge({
  status,
}: { status: TouchpointRecording["transcription_status"] }) {
  const cls =
    status === "succeeded"
      ? "text-teal"
      : status === "failed"
        ? "text-ember-ink"
        : "text-text-tertiary";
  const label =
    status === "succeeded"
      ? "transcribed"
      : status === "transcribing"
        ? "transcribing…"
        : status === "failed"
          ? "failed"
          : "queued";
  return <span className={cls}>{label}</span>;
}


function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}


function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}


function MetadataCard({ tp }: { tp: Touchpoint }) {
  return (
    <article
      data-testid="touchpoint-detail-metadata"
      className="flex flex-col gap-2 rounded-md border border-border-subtle bg-canvas/40 p-4 text-xs"
    >
      <h3 className="text-sm font-semibold text-text-primary">Metadata</h3>
      <MetaRow label="Created" value={formatDateOnly(tp.created_at)} />
      <MetaRow label="Updated" value={formatDateOnly(tp.updated_at)} />
      <MetaRow label="Completed" value={formatDateOnly(tp.completed_at)} />
    </article>
  );
}


function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-text-secondary">
      <span className="text-text-tertiary">{label}</span>
      <span>{value}</span>
    </div>
  );
}


function Field({
  label, value, multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <span
        className={
          multiline
            ? "whitespace-pre-wrap text-sm text-text-primary"
            : "text-sm text-text-primary"
        }
      >
        {value}
      </span>
    </div>
  );
}


function formatDateTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}


function formatDateOnly(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "numeric", day: "numeric", year: "numeric",
  });
}


function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Couldn't save the Touchpoint.";
}


